/**
 * Carica DDT senza ordine (magazzino/admin): l'AI estrae cliente, numero/data DDT e righe,
 * l'utente rivede e conferma → ordine retroattivo "Spedito" + documento in Documenti.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ScanLine, Search, Plus, X, Save, FileUp, UserCheck, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import NumberInput from "./NumberInput";
import { DatePickerIT } from "./DatePickerIT";
import { useCompany } from "../context/CompanyContext";
import { formatMoney } from "../utils/currency";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const itDateToIso = (s) => {
  if (!s) return "";
  const m = String(s).match(/(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})/);
  if (!m) return "";
  const yyyy = m[3].length === 2 ? `20${m[3]}` : m[3];
  return `${yyyy}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
};

export const DdtStandaloneDialog = ({ open, onClose, onCreated }) => {
  const { company, displayMode } = useCompany();
  const fm = (v) => formatMoney(v, company, { displayMode });

  const [file, setFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [scannedCustomer, setScannedCustomer] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [matchType, setMatchType] = useState(null);
  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [docNumber, setDocNumber] = useState("");
  const [docDate, setDocDate] = useState("");
  const [rows, setRows] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setFile(null); setScanning(false); setScanned(false); setScannedCustomer(null);
    setCustomer(null); setMatchType(null); setCustQuery(""); setCustResults([]);
    setDocNumber(""); setDocDate(""); setRows([]); setNotes(""); setSaving(false);
  };
  const close = () => { reset(); onClose(); };

  useEffect(() => {
    if (!custQuery || custQuery.length < 2) { setCustResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/customers?search=${encodeURIComponent(custQuery)}`);
        setCustResults((r.data || []).slice(0, 8));
      } catch { setCustResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [custQuery]);

  const scan = async () => {
    if (!file) { toast.error("Seleziona il PDF o la foto del DDT"); return; }
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/ddt/scan-standalone`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data;
      if (d.error) { toast.error(d.error); return; }
      setDocNumber(d.document_number || "");
      setDocDate(itDateToIso(d.document_date) || "");
      setRows((d.items || []).map((it) => ({
        code: it.code || "", description: it.description || "",
        quantity: it.quantity ?? 0, unit: it.unit || "Kg",
        unit_price: it.price ?? 0, discount_percent: it.discount_percent ?? 0,
        vat_rate: 0.10,
      })));
      setScannedCustomer(d.scanned_customer || null);
      setCustomer(d.matched_customer || null);
      setMatchType(d.match_type || null);
      setScanned(true);
      toast.success(`Letto DDT: ${(d.items || []).length} righe`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore lettura DDT");
    } finally { setScanning(false); }
  };

  const updRow = (i, patch) => setRows((prev) => prev.map((r, x) => (x === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((prev) => [...prev, { code: "", description: "", quantity: 0, unit: "Kg", unit_price: 0, discount_percent: 0, vat_rate: 0.10 }]);

  const isForeign = !!customer?.is_foreign;
  const lineTot = (r) => (r.quantity || 0) * (r.unit_price || 0) * (1 - (r.discount_percent || 0) / 100);
  const subtotal = rows.reduce((s, r) => s + lineTot(r), 0);
  const vatTotal = isForeign ? 0 : rows.reduce((s, r) => s + lineTot(r) * (r.vat_rate || 0), 0);

  const save = async () => {
    if (!customer) { toast.error("Seleziona il cliente"); return; }
    if (!docNumber.trim()) { toast.error("Inserisci il numero del DDT"); return; }
    const valid = rows.filter((r) => r.description.trim() && r.quantity > 0);
    if (valid.length === 0) { toast.error("Inserisci almeno una riga con descrizione e quantità"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify({
        customer_id: customer.id,
        document_number: docNumber.trim(),
        document_date: docDate || new Date().toISOString().slice(0, 10),
        items: valid,
        warehouse_notes: notes,
      }));
      if (file) fd.append("file", file);
      const r = await axios.post(`${API}/orders/from-ddt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Creato ordine ${r.data.order_number}${r.data.sales_document_number ? ` e documento ${r.data.sales_document_number}` : ""} ✅`);
      onCreated?.();
      close();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione ordine da DDT");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-4xl bg-white p-3 sm:p-6 min-h-[100dvh] max-h-[100dvh] sm:min-h-0 sm:max-h-[95vh] overflow-y-auto rounded-none sm:rounded-lg" data-testid="ddt-standalone-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <ScanLine className="w-5 h-5 text-[#8A1C2A]" /> Carica DDT senza ordine
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            Per ordini arrivati via mail/telefono: carichi il DDT, l&apos;AI estrae cliente e righe, e il sistema crea ordine spedito + documento.
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: upload */}
        <Card className="bg-stone-50 border-stone-200 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-2 text-sm border border-dashed border-stone-400 rounded-lg px-3 py-2 cursor-pointer hover:border-[#8A1C2A] hover:text-[#8A1C2A] bg-white">
              <FileUp className="w-4 h-4" /> {file ? file.name : "Scegli PDF o foto del DDT"}
              <input type="file" accept="application/pdf,image/*" className="hidden" data-testid="ddt-standalone-file-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            <Button onClick={scan} disabled={!file || scanning} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="ddt-standalone-scan-btn">
              <ScanLine className="w-4 h-4 mr-1" /> {scanning ? "Lettura AI in corso..." : "Analizza con AI"}
            </Button>
          </div>
        </Card>

        {scanned && (
          <>
            {/* Cliente */}
            <Card className={`p-3 ${customer ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-300"}`} data-testid="ddt-standalone-customer-card">
              {customer ? (
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <p className="text-sm font-semibold text-emerald-900 flex items-center gap-1.5">
                      <UserCheck className="w-4 h-4" /> {customer.business_name}
                      {matchType && <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">{matchType === "vat" ? "trovato per P.IVA" : "trovato per nome"}</Badge>}
                    </p>
                    <p className="text-xs text-stone-600">{customer.vat_number && `P.IVA ${customer.vat_number} · `}{customer.city || ""}</p>
                    <p className="text-xs text-stone-600">Agente: <strong>{customer.agent_name || "— diretto admin"}</strong></p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => { setCustomer(null); setMatchType(null); }} className="border-stone-300 h-8 text-xs" data-testid="ddt-standalone-change-customer">
                    Cambia
                  </Button>
                </div>
              ) : (
                <div>
                  <p className="text-sm font-semibold text-amber-900 flex items-center gap-1.5 mb-1">
                    <AlertTriangle className="w-4 h-4" /> Cliente non trovato automaticamente — cercalo in anagrafica
                  </p>
                  {scannedCustomer?.business_name && (
                    <p className="text-xs text-stone-600 mb-2">Dal DDT: <strong>{scannedCustomer.business_name}</strong>{scannedCustomer.vat_number && ` · P.IVA ${scannedCustomer.vat_number}`}</p>
                  )}
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input placeholder="Cerca cliente per nome o P.IVA..." value={custQuery} onChange={(e) => setCustQuery(e.target.value)} className="pl-8 bg-white border-stone-300" data-testid="ddt-standalone-customer-search" />
                    {custResults.length > 0 && (
                      <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                        {custResults.map((c) => (
                          <button key={c.id} type="button"
                            onClick={() => { setCustomer({ id: c.id, business_name: c.business_name, vat_number: c.vat_number, city: c.city, agent_name: null, is_foreign: c.is_foreign }); setCustQuery(""); setCustResults([]); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-stone-50 border-b border-stone-100"
                            data-testid={`ddt-standalone-customer-pick-${c.id}`}>
                            <span className="font-medium">{c.business_name}</span>
                            <span className="text-xs text-stone-500 ml-2">{c.vat_number} {c.city && `· ${c.city}`}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Card>

            {/* Testata DDT */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">📄 Numero DDT *</Label>
                <Input value={docNumber} onChange={(e) => setDocNumber(e.target.value)} placeholder="Es. 1234" className="bg-white border-stone-300 mt-1" data-testid="ddt-standalone-number" />
              </div>
              <div>
                <Label className="text-xs">📅 Data DDT</Label>
                <DatePickerIT value={docDate} onChange={setDocDate} data-testid="ddt-standalone-date" />
              </div>
            </div>

            {/* Righe */}
            <Card className="bg-white border-stone-200 overflow-hidden">
              <div className="px-3 py-2 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
                <p className="text-sm font-semibold">Righe articoli ({rows.length})</p>
                <Button size="sm" variant="outline" onClick={addRow} className="h-8 text-xs border-stone-300" data-testid="ddt-standalone-add-row">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Riga
                </Button>
              </div>
              <div className="divide-y divide-stone-100">
                {rows.map((r, i) => (
                  <div key={i} className="p-2.5" data-testid={`ddt-standalone-row-${i}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <Input value={r.code} onChange={(e) => updRow(i, { code: e.target.value })} placeholder="Codice" className="w-28 h-8 text-xs font-mono bg-white border-stone-300" />
                      <Input value={r.description} onChange={(e) => updRow(i, { description: e.target.value })} placeholder="Descrizione *" className="flex-1 min-w-[160px] h-8 text-xs bg-white border-stone-300" data-testid={`ddt-standalone-desc-${i}`} />
                      <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => setRows(rows.filter((_, x) => x !== i))} data-testid={`ddt-standalone-remove-${i}`}>
                        <X className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap items-end gap-2 mt-1.5">
                      <div>
                        <Label className="text-[10px] text-stone-500">Q.tà *</Label>
                        <NumberInput step="0.01" min="0" value={r.quantity} onChange={(e) => updRow(i, { quantity: parseFloat(e.target.value) || 0 })} className="w-24 h-8 text-right bg-white border-stone-300" data-testid={`ddt-standalone-qty-${i}`} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-stone-500">U.M.</Label>
                        <Select value={r.unit} onValueChange={(v) => updRow(i, { unit: v })}>
                          <SelectTrigger className="w-20 h-8 text-xs bg-white border-stone-300"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Kg">Kg</SelectItem>
                            <SelectItem value="Pz">Pz</SelectItem>
                            <SelectItem value="CT">CT</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px] text-stone-500">€/U.M.</Label>
                        <NumberInput step="0.01" min="0" value={r.unit_price} onChange={(e) => updRow(i, { unit_price: parseFloat(e.target.value) || 0 })} className="w-24 h-8 text-right bg-white border-stone-300" data-testid={`ddt-standalone-price-${i}`} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-stone-500">Sconto %</Label>
                        <NumberInput step="0.5" min="0" max="100" value={r.discount_percent} onChange={(e) => updRow(i, { discount_percent: parseFloat(e.target.value) || 0 })} className="w-20 h-8 text-right bg-white border-stone-300" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-stone-500">IVA</Label>
                        <NumberInput step="0.01" min="0" max="1" value={r.vat_rate} onChange={(e) => updRow(i, { vat_rate: parseFloat(e.target.value) || 0 })} className="w-20 h-8 text-right bg-white border-stone-300" />
                      </div>
                      <div className="ml-auto text-right">
                        <Label className="text-[10px] text-stone-500 block">Totale</Label>
                        <span className="font-semibold text-[#8A1C2A] text-sm">{fm(lineTot(r))}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-3 py-2 bg-stone-50 border-t border-stone-200 text-sm flex flex-wrap gap-x-6 gap-y-1 justify-end">
                <span>Imponibile: <strong>{fm(subtotal)}</strong></span>
                <span>IVA{isForeign ? " (estero 0%)" : ""}: {fm(vatTotal)}</span>
                <span className="font-bold text-[#8A1C2A]" data-testid="ddt-standalone-grand-total">Totale: {fm(subtotal + vatTotal)}</span>
              </div>
            </Card>

            <div>
              <Label className="text-xs">Note magazzino (facoltative)</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Es. ordine ricevuto via mail il..." className="bg-white border-stone-300 mt-1" data-testid="ddt-standalone-notes" />
            </div>
          </>
        )}

        <DialogFooter className="flex-row flex-wrap gap-2 justify-end sticky bottom-0 bg-white border-t border-stone-200 pt-3 -mx-3 px-3 pb-1 sm:static sm:mx-0 sm:px-0 sm:pb-0 sm:border-0 sm:pt-0 z-10">
          <Button variant="outline" onClick={close} className="border-stone-300">Annulla</Button>
          {scanned && (
            <Button onClick={save} disabled={saving} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white flex-1 sm:flex-none" data-testid="ddt-standalone-save-btn">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Creazione..." : "Crea ordine + documento"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DdtStandaloneDialog;
